import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Grid,
  Button,
  Alert,
  useTheme,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { auth, db } from '../firebase';
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  updateDoc,
} from 'firebase/firestore';
import { format } from 'date-fns';
import CreateContestModal from './CreateContestModal';
import ContestDetailsModal from './ContestDetailsModal';

const ContestsPage = () => {
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [userClientIds, setUserClientIds] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [contests, setContests] = useState([]);

  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedContest, setSelectedContest] = useState(null);
  const [contestSettings, setContestSettings] = useState(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsContest, setDetailsContest] = useState(null);

  const [viewMode, setViewMode] = useState('active');

  useEffect(() => {
    const fetchUserClients = async () => {
      setLoading(true);
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error('User not authenticated');

        const uid = currentUser.uid;
        const userDocRef = doc(db, 'users', uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) throw new Error('User data not found');

        const userData = userDocSnap.data();
        const cIds = userData.clientIds || [];
        setUserClientIds(cIds);
      } catch (error) {
        console.error('Error fetching user clients:', error);
        setErrorMsg('Failed to load clients. Please try again.');
        setLoading(false);
      }
    };
    fetchUserClients();
  }, []);

  useEffect(() => {
    const fetchClientDetails = async () => {
      if (userClientIds.length === 0) {
        setClientsData([]);
        setLoading(false); // Stop loading if there are no clients
        return;
      }

      try {
        const clientDetailsPromises = userClientIds.map(id => getDoc(doc(db, 'clients', id)));
        const clientDocSnaps = await Promise.all(clientDetailsPromises);

        const fetchedClientsData = clientDocSnaps.map(docSnap => {
          if (docSnap.exists()) {
            // CORRECTED: Use displayName and map it to the 'name' property for the modal
            return { id: docSnap.id, name: docSnap.data().displayName || `Client ${docSnap.id}` };
          }
          return { id: docSnap.id, name: `Client ${docSnap.id}` };
        });

        setClientsData(fetchedClientsData);
      } catch (error) {
        console.error('Error fetching client details:', error);
        setErrorMsg('Failed to load client details.');
      } finally {
        setLoading(false);
      }
    };

    fetchClientDetails();
  }, [userClientIds]);

  useEffect(() => {
    if (userClientIds.length === 0) {
      setContests([]);
      return;
    }
    setLoading(true);

    const fetchContests = async () => {
      try {
        const contestsRef = collection(db, 'contests');
        const allContests = [];
        
        // Using a single query with 'in' operator for efficiency if clientIds list is 10 or less
        // Note: Firestore 'in' queries are limited to 10 items. If more, the loop is safer.
        const q = query(contestsRef, where('clientIds', 'array-contains-any', userClientIds));
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach(docSnap => {
          allContests.push({ id: docSnap.id, ...docSnap.data() });
        });
        
        // Fallback for old single clientId field (can be combined with the loop below if needed)
        for (const cid of userClientIds) {
            const singleQ = query(contestsRef, where('clientId', '==', cid));
            const singleSnap = await getDocs(singleQ);
            singleSnap.forEach(docSnap => {
                allContests.push({ id: docSnap.id, ...docSnap.data() });
            });
        }

        const uniqueContests = Array.from(new Map(allContests.map(c => [c.id, c])).values());
        
        // Sort contests after fetching and combining
        uniqueContests.sort((a, b) => b.startDate.toDate() - a.startDate.toDate());
        
        setContests(uniqueContests);
      } catch (error) {
        console.error('Error fetching contests:', error);
        setErrorMsg('Failed to load contests. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchContests();
  }, [userClientIds]);

  const handleOpenModal = () => {
    if (clientsData.length === 0) {
      setErrorMsg('No clients available. Cannot create a contest.');
      return;
    }
    setErrorMsg('');
    setSuccessMsg('');
    setSelectedContest(null);
    setModalOpen(true);
  };

  const handleEditClick = (contest, e) => {
    e.stopPropagation();
    setSelectedContest(contest);
    setErrorMsg('');
    setSuccessMsg('');
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedContest(null);
  };

  const handleModalSubmit = async ({ basicInfo, ...settings }) => {
    setContestSettings(settings);
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const { title, description, startDate, endDate } = basicInfo;
      const contestsRef = collection(db, 'contests');
      const newContestData = {
        title,
        description,
        startDate,
        endDate,
        ...settings,
      };

      if (selectedContest) {
        const contestDocRef = doc(db, 'contests', selectedContest.id);
        await updateDoc(contestDocRef, newContestData);
        setSuccessMsg('Contest updated successfully!');
      } else {
        newContestData.createdAt = new Date();
        await addDoc(contestsRef, newContestData);
        setSuccessMsg('Contest created successfully!');
      }

      // Trigger a re-fetch of contests by momentarily clearing and resetting userClientIds
      const tempIds = [...userClientIds];
      setUserClientIds([]);
      setTimeout(() => setUserClientIds(tempIds), 0);

    } catch (error) {
      console.error('Error saving contest:', error);
      setErrorMsg('Failed to save contest. Please try again.');
    } finally {
      setLoading(false);
      setModalOpen(false);
      setSelectedContest(null);
      setContestSettings(null);
    }
  };

  const handleCardClick = (contest) => {
    setDetailsContest(contest);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setDetailsContest(null);
  };

  const now = new Date();
  const filteredContests = contests.filter((contest) => {
    const endDate = contest.endDate.toDate();
    return viewMode === 'active' ? endDate >= now : endDate < now;
  });

  if (loading && contests.length === 0 && clientsData.length === 0 && !errorMsg) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: '40px 20px',
        background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[200]} 100%)`,
        minHeight: '100vh'
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          fontFamily: 'Montserrat, sans-serif',
          textAlign: 'center',
          mb: 4
        }}
      >
        Contests
      </Typography>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 600, margin: '0 auto' }}>
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2, maxWidth: 600, margin: '0 auto' }}>
          {successMsg}
        </Alert>
      )}

      <Box
        sx={{
          maxWidth: 600,
          margin: '0 auto',
          mb: 6,
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Button
          variant="contained"
          onClick={handleOpenModal}
          sx={{
            textTransform: 'none',
            fontWeight: 'bold',
            paddingX: 3,
            paddingY: 1
          }}
          disabled={clientsData.length === 0}
        >
          Create Contest
        </Button>
      </Box>

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newValue) => {
            if (newValue !== null) setViewMode(newValue);
          }}
          aria-label="contest view mode"
        >
          <ToggleButton value="active" aria-label="active contests" sx={{ textTransform: 'none', fontWeight: 'bold' }}>
            Active Contests
          </ToggleButton>
          <ToggleButton value="archived" aria-label="archived contests" sx={{ textTransform: 'none', fontWeight: 'bold' }}>
            Archived Contests
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {filteredContests.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
          No {viewMode === 'active' ? 'active' : 'archived'} contests found.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredContests.map((contest) => (
            <Grid item xs={12} sm={6} md={4} key={contest.id}>
              <Card
                sx={{
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  transition: 'box-shadow 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  },
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
                onClick={() => handleCardClick(contest)}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {contest.title}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  {contest.description && (
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {contest.description}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Start: {format(contest.startDate.toDate(), 'MMM d, yyyy')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    End: {format(contest.endDate.toDate(), 'MMM d, yyyy')}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant="outlined"
                    onClick={(e) => handleEditClick(contest, e)}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    Edit Settings
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <CreateContestModal
        open={modalOpen}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        availableClients={clientsData}
        selectedContest={selectedContest}
      />

      <ContestDetailsModal
        open={detailsOpen}
        onClose={handleDetailsClose}
        contest={detailsContest}
      />
    </Box>
  );
};

export default ContestsPage;